import { ArrowRight } from '../icon/arrow-right';
import Image from '../image';
import Link from '../link';
import { TopFiveRecommendations } from '../types';
import { Recommendation } from './ItineraryBody';
import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import { AncilliariesRecommendationsList } from '../recommendations/ancilliaries-recommendations-caroussel';
import { useRecoTracking } from '../for-you/analytics/recoTracker';
import { useRecoImpressionBatchObserver } from '../for-you/hooks/useRecoImpression';

type RecommendationsSectionProps = {
	recommendations: Recommendation[] | null;
	propertyId: string | undefined;
	ancilliariesRecommendations?: TopFiveRecommendations[];
	placement?: string;
};

type RecommendationCardProps = {
	recommendation: Recommendation;
	placement?: string;
	position: number;
};

const RecomendationCard = (props: RecommendationCardProps) => {
	const { recommendation, placement, position } = props;
	const { addToRecommendationsCookie } = useTealium();
	const { trackEnhancementRecoClick } = useRecoTracking();
	const resolvedPlacement = placement ?? 'recommendations_section';

	const impressionRef = useRecoImpressionBatchObserver(
		{
			reco_placement: resolvedPlacement,
			reco_position: String(position),
			reco_enhancement_sku: recommendation.sku || 'na',
			enhancement_datalake_reco_id: recommendation.datalakeRecoId ?? 'na',
		},
		'ancillary'
	);

	return (
		<div role="listitem">
			<Link
				intent={'unstyled'}
				href={recommendation.url}
				ref={impressionRef}
				className="max-h-[150px] max-w-[150px]"
				aria-label={recommendation.name}
				tabIndex={0}
				onClick={event => {
					event.preventDefault();
					addToRecommendationsCookie({
						ancillary: recommendation.sku,
						datalakeRecoId: [
							{
								sku: recommendation.sku || 'na',
								datalakeRecoId: recommendation.datalakeRecoId ?? 'na',
								ors: recommendation.propertyOrsCode || 'na',
								type: 'ancillary',
							},
						],
					});
					trackEnhancementRecoClick({
						sku: recommendation.sku,
						name: recommendation.name,
						propertyName: recommendation.propertyOrsCode || 'na',
						propertyCode: recommendation.propertyOrsCode || 'na',
						enhancementDatalakeRecoId: recommendation.datalakeRecoId ?? '',
						placement: resolvedPlacement,
						position,
					});
					setTimeout(() => {
						window.location.href = recommendation.url;
					}, 150);
				}}
			>
				<Image
					roughSize="s"
					className="h-[150px] w-[150px] object-cover pb-[6px]"
					src={recommendation.imageUrl}
					alt={recommendation.imageAlt ?? recommendation.name}
				/>
			</Link>
			<p className="text-subtitle2">{recommendation.name}</p>
		</div>
	);
};

export function RecommendationsSection(props: RecommendationsSectionProps) {
	const { recommendations, propertyId, ancilliariesRecommendations, placement } = props;
	const t = useTranslations();

	if (!ancilliariesRecommendations?.length && !recommendations?.length) {
		return null;
	}

	return (
		<section
			className={cn([
				'flex items-center justify-center',
				ancilliariesRecommendations?.length ? '' : 'border-b-[1px] border-b-border-subtle',
			])}
			aria-label={t('Recommendations Section')}
		>
			{/* Recommendations Header */}
			<div className="pl-200 md:pl-300 py-[25px] md:py-200 flex items-center justify-center w-full">
				{/* Details */}
				<div className="w-full">
					{/* Property Name */}
					<div className="flex items-center justify-between pr-[15px] md:pr-[0px] mb-[20px]">
						<h2 className="md:tablet-heading-h4 text-h4">
							{t(
								ancilliariesRecommendations?.length
									? 'Recommended Experiences'
									: 'Our Recommendations'
							)}
						</h2>
						{propertyId ? (
							<Link
								intent={'unstyled'}
								href={`/${propertyId}/discover?_s_icmp=itineraryoverlay_viewallrecos`}
								className="flex gap-100 text-caption3"
								aria-label={t('View all recommendations')}
								tabIndex={0}
							>
								{t('VIEW ALL')}{' '}
								<div className="cursor-pointer">
									<ArrowRight />
								</div>
							</Link>
						) : null}
					</div>
					{/* Details */}
					{ancilliariesRecommendations?.length ? (
						<div className="w-full">
							<AncilliariesRecommendationsList
								placement={placement}
								ancilliariesRecommendations={ancilliariesRecommendations}
							/>
						</div>
					) : (
						<div
							className="flex flex-row md:items-center spacing-inline-s flex-wrap spacing-stack-s"
							role="list"
							aria-label={t('Recommendations list')}
						>
							{recommendations?.map((recommendation, index) =>
								recommendation?.url ? (
									<RecomendationCard
										placement={placement}
										position={index + 1}
										key={`recommendation-card-component-${index}-${recommendation.name}`}
										recommendation={recommendation}
									/>
								) : null
							)}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
